import { CircleTickIcon } from '@commenda/icons';
import { Progress, Separator } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import React from 'react';

export interface FormProgressStep<T extends string> {
  id: T;
  label: string;
}

export interface FormProgressProps<T extends string> {
  steps: FormProgressStep<T>[];
  currentStepIndex: number;
  className?: string;
}

export const FormProgress: React.FC<FormProgressProps<string>> = ({
  steps,
  currentStepIndex,
  className,
}) => {
  // Calculate progress percentage
  const progressPercentage = Math.min(
    100,
    (currentStepIndex / steps.length) * 100
  );

  return (
    <div
      className={cn(
        'w-[337px] bg-bg-primary border border-border-primary rounded-2xl',
        className
      )}
    >
      <div className="py-3xl px-xl">
        <p className="txt-small-regular text-text-tertiary mb-xl">
          {currentStepIndex} of {steps.length} complete
        </p>
        {/* Progress Bar */}
        <Progress
          className="rounded-full"
          accent="success"
          value={progressPercentage}
        />
      </div>

      <Separator className="border-border-primary" />

      {/* Step Items */}
      <div className="flex flex-col gap-1 py-xl">
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={cn(
              'flex items-center gap-md p-xl my-xs mx-xl rounded-xl',
              index < currentStepIndex
                ? 'text-text-primary'
                : index === currentStepIndex
                  ? 'text-text-primary bg-bg-primary-hover'
                  : 'text-text-primary'
            )}
          >
            {/* Step Icon */}
            <div
              className={cn(
                'flex items-center justify-center size-6 rounded-full text-sm font-medium',
                index < currentStepIndex
                  ? 'bg-utility-success-100 text-utility-success-600'
                  : index === currentStepIndex
                    ? 'bg-fg-secondary text-text-white'
                    : 'bg-bg-tertiary text-text-secondary'
              )}
            >
              {index < currentStepIndex ? (
                <CircleTickIcon className="text-good-to-go-100" />
              ) : (
                <span>{index + 1}</span>
              )}
            </div>

            {/* Step Label */}
            <span className="txt-small-medium text-text-primary">
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
